import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Field, TextArea } from '@/components/forge/Field'
import { ForgeButton } from '@/components/forge/ForgeButton'
import { ForgeDrawer } from '@/components/forge/ForgeDrawer'
import { Icon } from '@/components/forge/Icon'
import { PlaceField } from '@/components/forge/PlaceField'
import { ErrorState, SkeletonRows } from '@/components/forge/States'
import { Eyebrow, Num } from '@/components/forge/primitives'
import { usePermissionScopes } from '@/features/auth/permissions'
import { usePlaceNodes } from '@/features/hierarchy/queries'
import { fitsPlantRole } from '@/features/rbac/api'
import { useCreateRole, usePermissionCatalogue, useUpdateRole } from '@/features/rbac/queries'
import { useToast } from '@/providers/ToastProvider'
import { ON_TINT_CLASS, TINT_CLASS } from '@/lib/tone'
import { cn } from '@/lib/cn'

/**
 * Create a custom role.
 *
 * ── Three fields, because the API has three ──────────────────────────────
 * `RoleIn` is a name, a description and a list of permission ids. Both strings
 * are required — a description is not optional here, which is unusual enough to
 * be worth stating on the form rather than discovering as a 422.
 *
 * Everything else this drawer used to carry has no field behind it and is
 * commented out below rather than deleted: the colour tone, the "view only"
 * preset, and the reach/orphan warnings, which all depended on a catalogue this
 * product invented before the real one existed.
 *
 * ── The grid is the same grid ────────────────────────────────────────────
 * A card per module, pills inside it, All/Clear on the card and a count beside
 * it. That shape survives unchanged — what changed is what fills it. It used to
 * be the navigation rail with authored feature names; it is now the seeded
 * catalogue, where a module is a real grouping and a pill is a real capability
 * the server checks for.
 *
 * The pills carry their descriptions as tooltips, because the catalogue has one
 * for every permission and a name like "Manage Documents on Asset Classes" is
 * not self-explanatory to somebody deciding whether to grant it.
 *
 * ── One plant, or the whole account ─────────────────────────────────────
 * `RoleCreate` carries `plant_id`: null for a role that applies across the
 * account, or a plant that owns it. Same control as Shift Master, for the same
 * reasons — create-only (`RoleIn` does not declare it), and "every plant" needs
 * an account-level `users.manage` grant, so it is disabled rather than refused.
 *
 * A plant's role can only be assigned inside that plant, so the server refuses
 * permissions that attach at account level alone. The grid hides those while a
 * plant is chosen, and the submit drops any that were ticked before it was.
 *
 * ── Edit shares the form, and one refusal cannot be predicted ───────────
 * A system role is never opened here: the page knows `isSystem` and does not
 * offer the button. The other refusal is invisible until it happens — a role
 * somebody already HOLDS may be renamed but may not have its permissions
 * changed, and no field on the roles response says who holds what.
 *
 * So the form asks, and when the server refuses it says which rule was hit and
 * offers the one thing that would have worked: saving the name and description
 * with the permissions left as they were. That beats a banner telling somebody
 * to undo twenty clicks they cannot now see the original of.
 */

/* Commented out with the tone control below. The API has no colour on a role,
   so nothing would carry it.

const NO_TONE = '__none__'
const TONES = ['forge', 'ack', 'green', 'amber', 'violet', 'watch', 'dismissed', 'red']
*/

const SITE_ONLY = new Set(['site'])

/** One glyph per module key, so a card is recognisable before it is read. */
const MODULE_ICON = {
  account: 'gear',
  anomalies: 'bolt',
  assets: 'factory',
  copilot: 'ai',
  documents: 'doc',
  logbook: 'search',
  sensors: 'wave',
  shifts: 'cal',
  users: 'users',
  work_orders: 'wrench',
}

export function RoleEditorDrawer({ request, onClose, onSaved }) {
  if (!request) return null
  return (
    <Editor
      key={`${request.mode}-${request.role?.id ?? 'new'}`}
      request={request}
      onClose={onClose}
      onSaved={onSaved}
    />
  )
}

function Editor({ request, onClose, onSaved }) {
  const { t } = useTranslation(['app', 'common'])
  const { toast } = useToast()

  const editing = request.mode === 'edit'
  const source = request.role ?? null

  const catalogue = usePermissionCatalogue()
  const create = useCreateRole()
  const update = useUpdateRole()
  const save = editing ? update : create

  const grant = usePermissionScopes('users.manage')
  /* Held apart from `plantId` for the reason Shift Master gives: "one plant"
     with none picked yet is a real, unfinished state. */
  const [onePlant, setOnePlant] = useState(() =>
    editing ? source.plantId !== null : !grant.accountLevel,
  )
  const [plantId, setPlantId] = useState(source?.plantId ?? null)
  const [pickedName, setPickedName] = useState(null)
  /* On edit the plant is fixed and only needs naming. */
  const owner = usePlaceNodes(editing && plantId ? [{ tier: 'site', id: plantId }] : [])
  const plantName =
    pickedName ?? owner.data.items.find((node) => node.id === plantId)?.name ?? plantId
  const plantNotGranted =
    !editing && plantId !== null && !grant.accountLevel && !grant.plantIds.has(plantId)

  const [name, setName] = useState(source?.name ?? '')
  const [description, setDescription] = useState(source?.description ?? '')
  /* A Set of permission ids — which is what the API takes, so nothing has to be
     mapped back out of a keyed object at submit. */
  const [granted, setGranted] = useState(() => new Set(source?.permissionIds ?? []))

  /* What the role held when the form opened. Kept so the name-and-description
     fallback below can put the permissions back exactly as they were. */
  const originalIds = useMemo(() => source?.permissionIds ?? [], [source])

  const permissionsLocked = Boolean(save.error?.fields?.permission_ids)

  const modules = useMemo(() => {
    const all = catalogue.data?.modules ?? []
    if (!onePlant) return all
    return all
      .map((module) => ({ ...module, permissions: module.permissions.filter(fitsPlantRole) }))
      .filter((module) => module.permissions.length > 0)
  }, [catalogue.data, onePlant])
  const allIds = useMemo(
    () => modules.flatMap((module) => module.permissions.map((permission) => permission.id)),
    [modules],
  )

  /**
   * The server's own wording is never rendered: it is untranslated and can
   * describe internals. The field key is used as a signal and this copy says it
   * in the product's language.
   */
  const nameTaken = Boolean(save.error?.fields?.name)
  const plantRefused = Boolean(save.error?.fields?.plant_id)
  /* Only what the grid can show — ticks on permissions a plant hides are not sent. */
  const grantedCount = allIds.filter((id) => granted.has(id)).length

  function toggle(id) {
    setGranted((current) => {
      const next = new Set(current)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function toggleModule(module) {
    const ids = module.permissions.map((permission) => permission.id)
    const allOn = ids.every((id) => granted.has(id))
    setGranted((current) => {
      const next = new Set(current)
      for (const id of ids) {
        if (allOn) next.delete(id)
        else next.add(id)
      }
      return next
    })
  }

  function submit(permissionIds = [...granted].filter((id) => allIds.includes(id))) {
    save.mutate(
      {
        ...(editing ? { id: source.id } : { plantId: onePlant ? plantId : null }),
        name: name.trim(),
        description: description.trim(),
        permissionIds,
      },
      {
        onSuccess: (role) => {
          toast(t(editing ? 'settings.roles.updated' : 'settings.roles.created'), 'ok')
          onSaved(role)
          onClose()
        },
      },
    )
  }

  /* All three are required by the schema — `description` has `min_length=1`, so
     an empty one is a 422 rather than a null. A role with no permissions is
     accepted by the API but grants nothing, which the form says rather than
     refuses: an empty role is a legitimate starting point. */
  const canSubmit =
    Boolean(name.trim()) &&
    Boolean(description.trim()) &&
    (editing || !onePlant || (plantId !== null && !plantNotGranted)) &&
    !save.isPending

  return (
    <ForgeDrawer
      open
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
      title={
        editing
          ? t('settings.roles.editTitle', { name: source.name })
          : t('settings.roles.createTitle')
      }
      closeLabel={t('common:actions.close')}
      className="w-[min(38rem,100vw)]"
      footer={
        <>
          <ForgeButton onClick={onClose}>{t('common:actions.cancel')}</ForgeButton>
          <span className="flex-1" />
          <ForgeButton variant="primary" disabled={!canSubmit} onClick={() => submit()}>
            {save.isPending
              ? t('settings.roles.saving')
              : editing
                ? t('common:actions.save')
                : t('settings.roles.create')}
          </ForgeButton>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {/* A failed write, stated where it was attempted. A name collision is
            routed to the field; anything else lands here. */}
        {/* The one refusal nothing can predict: this role is assigned to
            somebody, so its permissions are frozen. The name and description are
            not — so rather than telling them to undo edits they can no longer
            see the original of, this offers the save that would have worked. */}
        {permissionsLocked ? (
          <div role="alert" className="rounded-sm border border-hairline stripe-amber bg-card p-3.5">
            <p className="m-0 text-control text-on-amber">{t('settings.roles.inUseTitle')}</p>
            <p className="mt-1 mb-0 text-body-sm text-ink-dim">{t('settings.roles.inUseBody')}</p>
            <ForgeButton
              size="sm"
              className="mt-2.5"
              disabled={save.isPending}
              onClick={() => submit(originalIds)}
            >
              {t('settings.roles.saveTextOnly')}
            </ForgeButton>
          </div>
        ) : save.isError && !nameTaken && !plantRefused ? (
          <div role="alert" className="rounded-sm border border-hairline stripe-red bg-card p-3.5">
            <p className="m-0 text-control text-on-red">
              {t(editing ? 'settings.roles.updateFailed' : 'settings.roles.saveFailed')}
            </p>
            <p className="mt-1 mb-0 text-body-sm text-ink-dim">
              {t('settings.roles.saveFailedHint')}
            </p>
          </div>
        ) : null}

        <Field
          id="role-name"
          label={t('settings.roles.fields.name')}
          required
          autoFocus
          maxLength={150}
          value={name}
          error={nameTaken ? t('settings.roles.fields.nameTaken') : null}
          onChange={(event) => setName(event.target.value)}
        />
        <TextArea
          id="role-description"
          label={t('settings.roles.fields.description')}
          rows={2}
          required
          maxLength={500}
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          hint={t('settings.roles.descriptionHint')}
        />

        <div>
          <Eyebrow as="p" className="mb-2">
            {t('settings.roles.fields.scope')}
          </Eyebrow>
          {editing ? (
            <div className="rounded-sm border border-hairline bg-sunken px-3 py-2.5">
              <p className="m-0 text-body-sm text-ink">
                {plantId === null ? t('settings.roles.scopeAccount') : plantName}
              </p>
              <p className="mt-1 mb-0 text-micro text-ink-mute">{t('settings.roles.scopeFixed')}</p>
            </div>
          ) : (
            <>
              <label className="flex cursor-pointer items-start gap-2 py-1 text-body-sm">
                <input
                  type="radio"
                  name="role-scope"
                  checked={!onePlant}
                  disabled={!grant.accountLevel}
                  onChange={() => {
                    setOnePlant(false)
                    setPlantId(null)
                  }}
                  className="mt-0.5 size-4 accent-[var(--hue-forge)]"
                />
                <span className="flex flex-col">
                  <span className={grant.accountLevel ? undefined : 'text-ink-mute'}>
                    {t('settings.roles.scopeAccount')}
                  </span>
                  <span className="text-micro text-ink-mute">
                    {grant.accountLevel
                      ? t('settings.roles.scopeAccountHint')
                      : t('settings.roles.scopeAccountNeedsGrant')}
                  </span>
                </span>
              </label>

              <label className="flex cursor-pointer items-start gap-2 py-1 text-body-sm">
                <input
                  type="radio"
                  name="role-scope"
                  checked={onePlant}
                  onChange={() => setOnePlant(true)}
                  className="mt-0.5 size-4 accent-[var(--hue-forge)]"
                />
                <span className="flex flex-col">
                  <span>{t('settings.roles.scopePlant')}</span>
                  <span className="text-micro text-ink-mute">
                    {t('settings.roles.scopePlantHint')}
                  </span>
                </span>
              </label>

              {onePlant ? (
                <PlaceField
                  className="mt-1 ms-6"
                  label={t('settings.roles.fields.plant')}
                  required
                  value={plantId ? { id: plantId, tier: 'site', name: plantName } : null}
                  allowTiers={SITE_ONLY}
                  placeholder={t('settings.roles.plantPlaceholder')}
                  onChange={(place) => {
                    setPickedName(place?.name ?? null)
                    setPlantId(place?.id ?? null)
                  }}
                  error={
                    plantNotGranted || plantRefused
                      ? t('settings.roles.plantNotGranted')
                      : undefined
                  }
                />
              ) : null}
            </>
          )}
        </div>

        {/* ── No field behind these ────────────────────────────────────────
            A role has a name, a description and permissions. It has no colour,
            so the tone picker would set something nothing stores.

        <div>
          <Eyebrow className="mb-1.5">{t('settings.roles.fields.tone')}</Eyebrow>
          <div className="flex flex-wrap gap-1.5">
            {[NO_TONE, ...TONES].map((entry) => (
              <button
                key={entry}
                type="button"
                aria-pressed={tone === entry}
                onClick={() => setTone(entry)}
                className={cn(
                  'rounded-pill border px-2.5 py-[3px] text-micro',
                  entry === NO_TONE
                    ? 'bg-sunken text-ink-dim'
                    : cn(TINT_CLASS[entry], ON_TINT_CLASS[entry]),
                  tone === entry ? 'border-forge' : 'border-transparent',
                )}
              >
                {entry === NO_TONE
                  ? t('settings.roles.noTone')
                  : t(`settings.roles.tones.${entry}`)}
              </button>
            ))}
          </div>
        </div>
        */}

        <div className="-mx-5 border-t border-hairline px-5 pt-4">
          <div className="flex items-center gap-2.5">
            {/* One tri-state box rather than Everything / Nothing buttons: it
                shows where the grid stands (none, some, all) as well as
                changing it. Some → ticking it grants the rest. */}
            <label className="flex cursor-pointer items-center gap-2.5">
              <TriStateCheckbox
                checked={allIds.length > 0 && grantedCount === allIds.length}
                indeterminate={grantedCount > 0 && grantedCount < allIds.length}
                disabled={allIds.length === 0}
                onChange={(event) =>
                  setGranted(event.target.checked ? new Set(allIds) : new Set())
                }
              />
              <Eyebrow>{t('settings.roles.permissionsLabel')}</Eyebrow>
            </label>
            <Num className="ms-auto text-body-sm text-ink-dim">
              {grantedCount} / {allIds.length}
            </Num>
          </div>

          {/* The reach and orphan warnings lived here. Both read a `gate`
              permission per module, which the invented catalogue declared and
              the real one does not — there is no permission that means "can
              open this screen", so nothing can be orphaned behind one. */}
          <p className="mt-2.5 mb-0 text-micro font-normal text-ink-mute">
            {grantedCount === 0
              ? t('settings.roles.noneGranted')
              : t('settings.roles.grantedSummary', { count: grantedCount })}
          </p>
        </div>

        {catalogue.isPending ? (
          <SkeletonRows count={5} />
        ) : catalogue.isError ? (
          <ErrorState
            error={catalogue.error}
            title={t('settings.roles.catalogueFailed')}
            onRetry={catalogue.refetch}
          />
        ) : (
          <ul className="m-0 flex list-none flex-col gap-2 p-0">
            {modules.map((module) => {
              const ids = module.permissions.map((permission) => permission.id)
              const on = ids.filter((id) => granted.has(id)).length
              const all = on === ids.length && ids.length > 0
              return (
                <li key={module.id} className="rounded-sm border border-hairline bg-card p-3">
                  <div className="flex items-center gap-2.5">
                    <TriStateCheckbox
                      checked={all}
                      indeterminate={on > 0 && !all}
                      aria-label={t('settings.roles.selectModule', { name: module.name })}
                      onChange={() => toggleModule(module)}
                    />
                    <Icon
                      name={MODULE_ICON[module.key] ?? 'layers'}
                      size="sm"
                      className={on ? 'text-on-forge' : 'text-ink-mute'}
                    />
                    <span className="min-w-0 flex-1">
                      {/* The catalogue is seeded in English and is not part of
                          the i18n bundle — these are the server's own names. */}
                      <span className="block truncate text-body-sm font-semibold text-ink">
                        {module.name}
                      </span>
                      <span className="block truncate text-micro font-normal text-ink-mute">
                        {module.description}
                      </span>
                    </span>
                    <Num className="text-micro font-normal text-ink-dim">
                      {on} / {ids.length}
                    </Num>
                  </div>

                  <div className="mt-2.5 flex flex-wrap gap-1.5 ps-[3.25rem]">
                    {module.permissions.map((permission) => {
                      const isGranted = granted.has(permission.id)
                      return (
                        <button
                          key={permission.id}
                          type="button"
                          aria-pressed={isGranted}
                          /* The catalogue carries a sentence for every
                             permission, and "Manage Documents on Asset Classes"
                             is not self-explanatory to somebody deciding whether
                             to grant it. */
                          title={permission.description}
                          onClick={() => toggle(permission.id)}
                          className={cn(
                            'flex items-center gap-1.5 rounded-pill border px-2.5 py-[3px] text-micro whitespace-nowrap transition-colors',
                            isGranted
                              ? cn(TINT_CLASS.green, ON_TINT_CLASS.green, 'border-transparent')
                              : 'border-hairline text-ink-dim hover:bg-sunken',
                          )}
                        >
                          <Icon name={isGranted ? 'check' : 'plus'} size="xs" />
                          {permission.name}
                        </button>
                      )
                    })}
                  </div>
                </li>
              )
            })}
          </ul>
        )}
      </div>
    </ForgeDrawer>
  )
}

/**
 * A native checkbox that can also show "some". `indeterminate` is a DOM
 * property with no HTML attribute, so it is set through the ref.
 */
function TriStateCheckbox({ indeterminate = false, className, ...props }) {
  return (
    <input
      type="checkbox"
      ref={(node) => {
        if (node) node.indeterminate = indeterminate
      }}
      className={cn('size-4 shrink-0 cursor-pointer accent-[var(--hue-forge)]', className)}
      {...props}
    />
  )
}
